import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { UserConsents } from '@/shared/api/protocol-ts/model/dto_user_pb';
import { LocalStorageKey, PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { getHubspotUserToken } from '@/shared/lib';

import { authModel } from '@/entities/auth';

import { ConfirmPasswordHandleType, FinishRegistrationMethod } from '../config';

export const useFinishRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();

  // type casting is necessary for typification location.state
  const invitationToken = (location?.state as { invitationToken: string })
    ?.invitationToken;

  const isInviteFromSalesman = (
    location?.state as { isInviteFromSalesman: string }
  )?.isInviteFromSalesman;

  const pendingMFARequirement = useAppSelector(
    authModel.selectors.selectPendingMFARequirement,
  );

  const authenticationMethod = useMemo(() => {
    if (isInviteFromSalesman) {
      return FinishRegistrationMethod.signUpViaSalesman;
    }

    return FinishRegistrationMethod.confirmPassword;
  }, [isInviteFromSalesman]);

  const completeRegistration = () => {
    dispatch(authModel.actions.clearPendingMFARequirement());

    localStorage.removeItem(LocalStorageKey.signUpFormData);
    localStorage.removeItem(LocalStorageKey.UTMParameters);

    navigate(
      isInviteFromSalesman ? PATHS.companySettings : PATHS.additionalInfo,
    );
  };

  const confirmPasswordHandle = async ({
    password,
    marketingCommunicationConsent,
  }: ConfirmPasswordHandleType) => {
    try {
      const confirmPasswordResponse = await dispatch(
        authModel.thunks.confirmPassword({
          Password: password,
          Consents: new UserConsents({
            MarketingConsent: marketingCommunicationConsent || false,
          }),
          HubspotUTK: getHubspotUserToken(),
        }),
      ).unwrap();

      if (confirmPasswordResponse.Status === AuthState_AuthStatus.SUCCESSFUL) {
        completeRegistration();
      }
    } catch (error) {
      // TODO: [1/l]  Investigate reason of this behaviour
      // We need a catch for useForm's isSubmitting to work correctly
    }
  };

  const signUpViaSalesmanHandle = async (password: string) => {
    try {
      const signUpViaSalesmanResponse = await dispatch(
        authModel.thunks.signUpViaSalesmanInvite({
          InviteToken: invitationToken,
          Password: password,
        }),
      ).unwrap();

      if (
        signUpViaSalesmanResponse.Status === AuthState_AuthStatus.SUCCESSFUL
      ) {
        completeRegistration();
      }
    } catch (error) {
      // TODO: [1/l]  Investigate reason of this behaviour
      // We need a catch for useForm's isSubmitting to work correctly
    }
  };

  return {
    authenticationMethod,
    isInviteFromSalesman,
    pendingMFARequirement,
    completeRegistration,
    confirmPasswordHandle,
    signUpViaSalesmanHandle,
  };
};
